/**
 * MatchRulesEditor — Collapsible card for editing match_rules on experiences.
 * Single chip list with operator cycling: Off → OR → AND → NOT → Off.
 * Tags grouped using shared taxonomy from placeTagGroups.ts.
 *
 * SoT: docs/architecture/smart-steering-tag-operators.md
 *      docs/architecture/smart-steering-sot.md
 */
import { useState, useEffect, useMemo } from 'react';
import { EXPLORE_DEBUG } from '@/lib/debug';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Bug } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Settings2, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePlaceIntentOptions } from '@/hooks/usePlaceIntentOptions';
import { PLACE_TAG_GROUPS, ALL_GROUPED_SLUGS, EXCLUDED_TAG_SLUGS } from '@/lib/tags/placeTagGroups';

/* ── Types ── */

interface MatchRules {
  any_tags: string[];
  all_tags: string[];
  exclude_tags: string[];
}

type TagOperator = 'off' | 'or' | 'and' | 'not';

interface TagMeta { slug: string; label: string }

interface MatchRulesEditorProps {
  entityId: string;
  matchRules?: Record<string, unknown> | null;
}

/* ── Constants ── */

const EMPTY_RULES: MatchRules = { any_tags: [], all_tags: [], exclude_tags: [] };

const OPERATOR_CYCLE: TagOperator[] = ['off', 'or', 'and', 'not'];

/* Curation limits – keep signal sharp */
const MAX_TOTAL_ACTIVE = 6;
const MAX_AND = 2;
const MAX_OR = 4;
const MAX_NOT = 4;
const OPERATOR_LABEL: Record<TagOperator, string> = {
  off: '',
  or: 'Någon',
  and: 'Måste',
  not: 'Inte',
};

/* ── Legacy slug mapping (non-canonical → canonical) ── */

const LEGACY_SLUG_MAP: Record<string, string> = {
  family: 'familj',
  kid_friendly: 'barnvanligt',
  ski: 'skidor',
  hiking: 'vandring',
  cycling: 'cykling',
  fishing: 'fiske',
  nature: 'naturreservat',
  culture: 'kultur-historia',
};

function canonicalSlug(slug: string): string {
  return LEGACY_SLUG_MAP[slug] ?? slug;
}

/* ── Helpers ── */

function normalizeTagArray(arr: unknown): string[] {
  if (!Array.isArray(arr)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const v of arr) {
    if (typeof v !== 'string') continue;
    const trimmed = canonicalSlug(v.trim());
    if (trimmed.length === 0 || seen.has(trimmed)) continue;
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}

function normalizeRules(raw: Record<string, unknown> | null | undefined, validSlugs?: Set<string>): MatchRules {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_RULES };
  const isValid = (s: string) => !validSlugs || validSlugs.has(s);
  // Support both old (tags_any/tags_all/tags_exclude) and new (any_tags/all_tags/exclude_tags) field names
  const exclude_tags = normalizeTagArray(raw.exclude_tags ?? raw.tags_exclude).filter(isValid);
  const excludeSet = new Set(exclude_tags);
  const all_tags = normalizeTagArray(raw.all_tags ?? raw.tags_all).filter((t) => isValid(t) && !excludeSet.has(t));
  const allSet = new Set(all_tags);
  const any_tags = normalizeTagArray(raw.any_tags ?? raw.tags_any).filter((t) => isValid(t) && !excludeSet.has(t) && !allSet.has(t));
  return { any_tags, all_tags, exclude_tags };
}

function isRulesActive(r: MatchRules): boolean {
  return r.any_tags.length > 0 || r.all_tags.length > 0 || r.exclude_tags.length > 0;
}

function getOperator(slug: string, rules: MatchRules): TagOperator {
  if (rules.exclude_tags.includes(slug)) return 'not';
  if (rules.all_tags.includes(slug)) return 'and';
  if (rules.any_tags.includes(slug)) return 'or';
  return 'off';
}

function cycleOperator(current: TagOperator): TagOperator {
  const idx = OPERATOR_CYCLE.indexOf(current);
  return OPERATOR_CYCLE[(idx + 1) % OPERATOR_CYCLE.length];
}

function setTagOperator(rules: MatchRules, slug: string, op: TagOperator): MatchRules {
  const next: MatchRules = {
    any_tags: rules.any_tags.filter((s) => s !== slug),
    all_tags: rules.all_tags.filter((s) => s !== slug),
    exclude_tags: rules.exclude_tags.filter((s) => s !== slug),
  };
  if (op === 'or') next.any_tags = [...next.any_tags, slug];
  else if (op === 'and') next.all_tags = [...next.all_tags, slug];
  else if (op === 'not') next.exclude_tags = [...next.exclude_tags, slug];
  return next;
}

function buildSummary(rules: MatchRules, tagMap: Map<string, string>): string | null {
  const parts: string[] = [];
  if (rules.any_tags.length > 0) parts.push(`Matchar om: ${rules.any_tags.map((s) => tagMap.get(s) ?? s).join(', ')}`);
  if (rules.all_tags.length > 0) parts.push(`Måste ha: ${rules.all_tags.map((s) => tagMap.get(s) ?? s).join(', ')}`);
  if (rules.exclude_tags.length > 0) parts.push(`Exkluderar: ${rules.exclude_tags.map((s) => tagMap.get(s) ?? s).join(', ')}`);
  return parts.length > 0 ? parts.join(' • ') : null;
}

/* ── Main component ── */

export function MatchRulesEditor({ entityId, matchRules: initialRules }: MatchRulesEditorProps) {
  const [open, setOpen] = useState(false);
  const [rules, setRules] = useState<MatchRules>(() => normalizeRules(initialRules as any));
  const [filter, setFilter] = useState('');
  const queryClient = useQueryClient();

  const { options: intentOptions } = usePlaceIntentOptions();
  const tags: TagMeta[] = useMemo(
    () => intentOptions.filter((o) => !EXCLUDED_TAG_SLUGS.has(o.slug)).map((o) => ({ slug: o.slug, label: o.label })),
    [intentOptions],
  );
  const tagMap = useMemo(() => new Map(tags.map((t) => [t.slug, t.label])), [tags]);
  const tagSet = useMemo(() => new Set(tags.map((t) => t.slug)), [tags]);

  // Re-normalize when initialRules or catalog changes — filter against known slugs
  useEffect(() => {
    if (tagSet.size > 0) {
      setRules(normalizeRules(initialRules as any, tagSet));
    }
  }, [entityId, initialRules, tagSet]);

  // Active (selected) tags sorted AND → OR → NOT
  const selectedTags = useMemo(() => {
    const operatorOrder: Record<TagOperator, number> = { and: 0, or: 1, not: 2, off: 3 };
    return tags
      .filter((t) => getOperator(t.slug, rules) !== 'off')
      .sort((a, b) => {
        const d = operatorOrder[getOperator(a.slug, rules)] - operatorOrder[getOperator(b.slug, rules)];
        return d !== 0 ? d : a.label.localeCompare(b.label, 'sv');
      });
  }, [tags, rules]);

  const active = isRulesActive(rules);
  const summary = useMemo(() => buildSummary(rules, tagMap), [rules, tagMap]);

  // Match count
  const savedRules = useMemo(() => normalizeRules(initialRules as any, tagSet), [initialRules, tagSet]);
  const savedActive = isRulesActive(savedRules);

  const { data: matchCount } = useQuery({
    queryKey: ['match-rules-count', 'experience', entityId, savedRules],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_experience_place_suggestions', { p_experience_slug: entityId, p_limit: 50 });
      if (error) {
        if (EXPLORE_DEBUG) console.warn('[Smart styrning] RPC error:', error.message);
        throw error;
      }
      const count = (data ?? []).length as number;
      if (EXPLORE_DEBUG) {
        console.log(`[Smart styrning] get_experience_place_suggestions(${entityId}) → ${count} platser`, {
          savedRules,
          results: data,
        });
      }
      return count;
    },
    enabled: savedActive,
    staleTime: 10_000,
  });

  const saveMut = useMutation({
    mutationFn: async (newRules: MatchRules) => {
      const normalized = normalizeRules(newRules as any, tagSet);
      const { error } = await supabase.from('experiences').update({ match_rules: normalized as any }).eq('slug', entityId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Matchning uppdaterad');
      queryClient.invalidateQueries({ queryKey: ['experience-match-rules', entityId] });
      queryClient.invalidateQueries({ queryKey: ['tag-suggestions', 'experience', entityId] });
      queryClient.invalidateQueries({ queryKey: ['match-rules-count', 'experience', entityId] });
    },
    onError: (e: any) => toast.error(e.message ?? 'Kunde inte spara matchning'),
  });

  const handleCycleTag = (slug: string) => {
    setRules((prev) => {
      const currentOp = getOperator(slug, prev);
      let nextOp = cycleOperator(currentOp);

      // Enforce curation limits — skip operators that are at max
      const totalActive = prev.any_tags.length + prev.all_tags.length + prev.exclude_tags.length;
      const isCurrentlyActive = currentOp !== 'off';
      let attempts = 0;
      while (attempts < 4) {
        if (nextOp === 'off') break; // always allowed
        const wouldBeNewActive = !isCurrentlyActive || nextOp !== currentOp;
        const newTotal = isCurrentlyActive ? totalActive : totalActive + 1;
        const orCount = nextOp === 'or' ? prev.any_tags.filter(s => s !== slug).length + 1 : prev.any_tags.filter(s => s !== slug).length;
        const andCount = nextOp === 'and' ? prev.all_tags.filter(s => s !== slug).length + 1 : prev.all_tags.filter(s => s !== slug).length;
        const notCount = nextOp === 'not' ? prev.exclude_tags.filter(s => s !== slug).length + 1 : prev.exclude_tags.filter(s => s !== slug).length;

        const overTotal = wouldBeNewActive && newTotal > MAX_TOTAL_ACTIVE;
        const overOr = nextOp === 'or' && orCount > MAX_OR;
        const overAnd = nextOp === 'and' && andCount > MAX_AND;
        const overNot = nextOp === 'not' && notCount > MAX_NOT;

        if (!overTotal && !overOr && !overAnd && !overNot) break;
        nextOp = cycleOperator(nextOp);
        attempts++;
      }

      return setTagOperator(prev, slug, nextOp);
    });
  };

  const isDirty = useMemo(() => {
    const original = normalizeRules(initialRules as any, tagSet);
    return (
      JSON.stringify(original.any_tags.sort()) !== JSON.stringify([...rules.any_tags].sort()) ||
      JSON.stringify(original.all_tags.sort()) !== JSON.stringify([...rules.all_tags].sort()) ||
      JSON.stringify(original.exclude_tags.sort()) !== JSON.stringify([...rules.exclude_tags].sort())
    );
  }, [rules, initialRules]);

  // Filter
  const filterLower = filter.toLowerCase().trim();

  return (
    <div className="mt-6 pt-5 border-t border-dashed">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left group">
          <Settings2 className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
            Smart styrning
          </h3>
          {active && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-normal">Aktiv</Badge>
          )}
          <span className="ml-auto">
            {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
          </span>
        </CollapsibleTrigger>

        <CollapsibleContent className="mt-3 space-y-3">
          {/* Summary / empty state */}
          {!active ? (
            <div className="text-xs text-muted-foreground space-y-1 py-1">
              <p className="font-medium text-foreground/70">Ingen smart styrning aktiv.</p>
              <p>Platser du lägger till manuellt visas alltid. Klicka på taggar för att styra förslag.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {summary && <p className="text-xs text-foreground/70 leading-relaxed">{summary}</p>}
              <p className="text-[11px] text-muted-foreground">
                Klicka för att cykla: <span className="font-medium">Någon → Måste → Inte → Av</span>
              </p>
              {selectedTags.length >= MAX_TOTAL_ACTIVE && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                  Håll styrningen skarp – max {MAX_TOTAL_ACTIVE} aktiva taggar.
                </p>
              )}
            </div>
          )}

          {/* Match count + debug toggle */}
          {savedActive && typeof matchCount === 'number' && (
            <div className="space-y-1">
              <p className={cn('text-xs', matchCount > 0 ? 'text-muted-foreground' : 'text-destructive/80')}>
                {matchCount > 0
                  ? `${matchCount} ${matchCount === 1 ? 'plats matchar' : 'platser matchar'} nuvarande regler`
                  : '0 platser matchar — prova fler eller justera taggar'}
              </p>
              {EXPLORE_DEBUG && (
                <MatchDebugPanel experienceSlug={entityId} />
              )}
            </div>
          )}

          {/* Selected tags (quick overview) */}
          {selectedTags.length > 0 && (
            <div className="space-y-1">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Valda taggar</span>
              <div className="flex flex-wrap gap-1.5">
                {selectedTags.map((tag) => (
                  <OperatorChip key={tag.slug} label={tag.label} operator={getOperator(tag.slug, rules)} onClick={() => handleCycleTag(tag.slug)} />
                ))}
              </div>
            </div>
          )}

          {/* Search */}
          {tags.length > 8 && (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrera taggar…" className="h-7 pl-8 text-xs" />
            </div>
          )}

          {/* Grouped tags */}
          {tags.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Inga taggar ännu</p>
          ) : (
            <div className="space-y-1">
              {PLACE_TAG_GROUPS.map((group) => {
                const groupTags = tags.filter((t) => group.slugs.includes(t.slug));
                if (groupTags.length === 0) return null;

                const selectedInGroup = groupTags.filter((t) => getOperator(t.slug, rules) !== 'off').length;
                const hasFilterMatch = !filterLower || groupTags.some((t) => t.label.toLowerCase().includes(filterLower));

                // Filter: hide groups with no matches (unless they have selections)
                if (filterLower && !hasFilterMatch && selectedInGroup === 0) return null;

                return (
                  <TagGroupCollapsible
                    key={group.key}
                    icon={group.icon}
                    title={group.title}
                    selectedCount={selectedInGroup}
                    defaultOpen={selectedInGroup > 0 || (!!filterLower && hasFilterMatch)}
                  >
                    <div className="flex flex-wrap gap-1.5">
                      {groupTags
                        .filter((t) => !filterLower || t.label.toLowerCase().includes(filterLower) || getOperator(t.slug, rules) !== 'off')
                        .map((tag) => (
                          <OperatorChip key={tag.slug} label={tag.label} operator={getOperator(tag.slug, rules)} onClick={() => handleCycleTag(tag.slug)} />
                        ))}
                    </div>
                  </TagGroupCollapsible>
                );
              })}

              {/* Ungrouped safety net */}
              {(() => {
                const ungrouped = tags.filter((t) => !ALL_GROUPED_SLUGS.has(t.slug));
                if (ungrouped.length === 0) return null;
                const selectedInGroup = ungrouped.filter((t) => getOperator(t.slug, rules) !== 'off').length;
                const filtered = ungrouped.filter((t) => !filterLower || t.label.toLowerCase().includes(filterLower) || getOperator(t.slug, rules) !== 'off');
                if (filtered.length === 0) return null;
                return (
                  <TagGroupCollapsible icon="📌" title="Övrigt" selectedCount={selectedInGroup} defaultOpen={selectedInGroup > 0}>
                    <div className="flex flex-wrap gap-1.5">
                      {filtered.map((tag) => (
                        <OperatorChip key={tag.slug} label={tag.label} operator={getOperator(tag.slug, rules)} onClick={() => handleCycleTag(tag.slug)} />
                      ))}
                    </div>
                  </TagGroupCollapsible>
                );
              })()}
            </div>
          )}

          {/* Save / undo */}
          {isDirty && (
            <div className="space-y-2 pt-1">
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => saveMut.mutate(rules)} disabled={saveMut.isPending}>
                  {saveMut.isPending ? 'Sparar…' : 'Spara matchning'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setRules(normalizeRules(initialRules as any, tagSet))} disabled={saveMut.isPending}>
                  Ångra
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Förslag uppdateras när du sparar matchningen.</p>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/* ── Tag group collapsible ── */

function TagGroupCollapsible({
  icon,
  title,
  selectedCount,
  defaultOpen,
  children,
}: {
  icon: string;
  title: string;
  selectedCount: number;
  defaultOpen: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // Auto-open when selections change
  useEffect(() => {
    if (defaultOpen) setIsOpen(true);
  }, [defaultOpen]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left py-1 group">
        <span className="text-xs">{icon}</span>
        <span className="text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">{title}</span>
        {selectedCount > 0 && (
          <span className="text-[10px] text-primary font-medium">({selectedCount})</span>
        )}
        <span className="ml-auto">
          {isOpen ? <ChevronDown className="h-3 w-3 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 text-muted-foreground" />}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-5 pb-1.5 pt-1">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

/* ── Operator chip ── */

function OperatorChip({
  label,
  operator,
  onClick,
}: {
  label: string;
  operator: TagOperator;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border transition-all cursor-pointer select-none',
        operator === 'off' && 'bg-muted/40 border-border text-muted-foreground hover:bg-muted hover:border-muted-foreground/30',
        operator === 'or' && 'bg-primary/10 border-primary/30 text-primary shadow-sm',
        operator === 'and' && 'bg-primary/20 border-primary/50 text-primary font-semibold shadow-sm',
        operator === 'not' && 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400 shadow-sm',
      )}
      title={
        operator === 'off' ? 'Klicka för "Någon"'
          : operator === 'or' ? 'Någon av dessa – klicka för "Måste"'
          : operator === 'and' ? 'Måste ha – klicka för "Inte"'
          : 'Exkludera – klicka för att ta bort'
      }
    >
      {operator !== 'off' && (
        <span className={cn(
          'text-[9px] uppercase tracking-wider font-bold leading-none rounded px-1 py-0.5',
          operator === 'or' && 'bg-primary/15 text-primary',
          operator === 'and' && 'bg-primary/25 text-primary',
          operator === 'not' && 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
        )}>
          {OPERATOR_LABEL[operator]}
        </span>
      )}
      {label}
    </button>
  );
}

/* ── Debug panel (dev only) ── */

interface DebugRow {
  place_id: string;
  slug: string;
  name: string;
  municipality: string | null;
  landskap: string | null;
  status: string;
  intents: string[];
  is_match: boolean;
  excluded_reason: string | null;
  matched_intents: string[];
  required_intents: string[];
  missing_intents: string[];
}

const REASON_LABELS: Record<string, string> = {
  status_archived: 'Arkiverad',
  no_intents: 'Inga intents',
  intents_null: 'Intents saknas',
  excluded_by_not_tags: 'Exkluderad (INTE-tagg)',
  missing_must_tags: 'Saknar MÅSTE-tagg',
  no_matching_any_tags: 'Ingen NÅGON-tagg matchar',
  already_linked: 'Redan tillagd',
};

function MatchDebugPanel({ experienceSlug }: { experienceSlug: string }) {
  const [showDebug, setShowDebug] = useState(false);

  const { data: debugRows, isLoading } = useQuery({
    queryKey: ['debug-experience-match', experienceSlug],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('debug_experience_match', {
        p_experience_slug: experienceSlug,
        p_limit: 30,
      });
      if (error) throw error;
      return (data ?? []) as DebugRow[];
    },
    enabled: showDebug,
    staleTime: 5_000,
  });

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setShowDebug(!showDebug)}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
      >
        <Bug className="h-3 w-3" />
        {showDebug ? 'Dölj debug' : 'Visa debug'}
      </button>

      {showDebug && (
        <div className="mt-2 rounded-md border bg-muted/30 p-2 text-[11px] space-y-1 max-h-64 overflow-y-auto">
          {isLoading && <p className="text-muted-foreground italic">Laddar…</p>}
          {debugRows && debugRows.length === 0 && (
            <p className="text-muted-foreground italic">Inga platser i databasen (exkl. arkiverade)</p>
          )}
          {debugRows && debugRows.map((row) => (
            <div
              key={row.place_id}
              className={cn(
                'flex items-start gap-2 px-2 py-1 rounded',
                row.is_match ? 'bg-emerald-500/10' : 'bg-muted/50'
              )}
            >
              <span className={cn('shrink-0 mt-0.5 w-2 h-2 rounded-full', row.is_match ? 'bg-emerald-500' : 'bg-muted-foreground/30')} />
              <div className="flex-1 min-w-0">
                <span className="font-medium">{row.name}</span>
                <span className="text-muted-foreground ml-1">({row.status})</span>
                {row.excluded_reason && !row.is_match && (
                  <span className="ml-1 text-destructive/70">
                    — {REASON_LABELS[row.excluded_reason] ?? row.excluded_reason}
                  </span>
                )}
                <div className="text-muted-foreground">
                  intents: [{Array.isArray(row.intents) ? row.intents.join(', ') : String(row.intents)}]
                  {row.missing_intents?.length > 0 && (
                    <span className="text-amber-600 dark:text-amber-400 ml-1">
                      saknar: [{row.missing_intents.join(', ')}]
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
